import * as DSAssetFamilies from '../../../filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { AUTOMATION_PLATFORM_FLOW_TYPE_ID, CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { STRING } from 'customer-data-objects/property/PropertyTypes';
import { NONE } from 'customer-data-filters-ui/filterQueryFormat/timestamps/TimestampTypes';
import { AdapterCache } from '../../requestCaches/adaptersCache';
import { Map as ImmutableMap, fromJS } from 'immutable';
import { hasPaginatedWorkflows } from '../../../permissions/gates';
import { toDSFieldRecord } from '../../../filterQueryFormat/DSFieldRecord/toDSFieldRecord';
import Adapter from './Adapter';
import MissingField from '../../../filterQueryFormat/MissingField';
import PortalIdParser from 'PortalIdParser';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'refe... Remove this comment to see the full error message
import ReferenceRecord from 'reference-resolvers/schema/ReferenceRecord';
import http from 'hub-http/clients/apiClient';
const isQueryChanged = (query, lastQuery) => (!!query || !!lastQuery) && query !== lastQuery;
const getMissingFields = (data, getMissingFieldErrorMessage, requiredIds = []) => {
  const missingFields = requiredIds.reduce((acc, id) => {
    if (!data.has(id)) {
      return acc.set(id, MissingField({
        label: id,
        name: id,
        type: STRING,
        timestampType: NONE
      }));
    }
    return acc;
  }, ImmutableMap());
  if (getMissingFieldErrorMessage) {
    return missingFields.map(field => field.set('errorMessage', getMissingFieldErrorMessage())).toMap();
  }
  return missingFields;
};
const createWorkflowRequestBody = (ids, query, offset = 0) => {
  const baseFilters = [{
    property: 'hs_visible_source_app',
    operator: 'EQ',
    value: 'true'
  }, {
    property: 'hs_object_type_id',
    operator: 'EQ',
    value: CONTACT_TYPE_ID
  }, {
    property: 'hs_is_deleted',
    operator: 'EQ',
    value: 'false'
  }];
  if (ids && ids.length > 0) {
    baseFilters.push({
      property: 'hs_classic_workflow_id',
      operator: 'IN',
      values: ids
    });
  } else if (query) {
    baseFilters.push({
      property: 'hs_name',
      operator: 'EQ',
      value: `${query}*`
    });
  }
  return {
    objectTypeId: AUTOMATION_PLATFORM_FLOW_TYPE_ID,
    filterGroups: [{
      filters: baseFilters
    }],
    requestOptions: {
      properties: ['hs_name', 'hs_classic_workflow_id']
    },
    sorts: [{
      property: 'hs_name',
      order: 'ASC'
    }],
    count: (ids === null || ids === void 0 ? void 0 : ids.length) || 100,
    offset
  };
};
const processWorkflowResponse = response => {
  const workflows = response.results || [];
  const referenceRecords = workflows.filter(workflow => {
    var _workflow$properties;
    return !!((_workflow$properties = workflow.properties) !== null && _workflow$properties !== void 0 && (_workflow$properties = _workflow$properties.hs_classic_workflow_id) !== null && _workflow$properties !== void 0 && _workflow$properties.value);
  }).map(workflow => {
    const {
      hs_classic_workflow_id,
      hs_name
    } = workflow.properties;
    return ReferenceRecord({
      id: hs_classic_workflow_id.value,
      label: hs_name.value,
      referencedObject: fromJS(workflow)
    });
  });
  return {
    referenceRecords,
    response
  };
};
const fetchWorkflowsByIds = (httpClient, portalId, ids, getMissingFieldErrorMessage) => {
  const requestBody = createWorkflowRequestBody(ids, undefined, 0);
  return httpClient(`crm-search/search?portalId=${portalId}`, {
    data: requestBody
  }).then(response => {
    const {
      referenceRecords
    } = processWorkflowResponse(response);
    const data = referenceRecords.reduce((acc, record) => {
      return acc.set(record.id, record);
    }, ImmutableMap());
    const missingData = getMissingFields(data, getMissingFieldErrorMessage, ids);
    return {
      data,
      hasMore: false,
      offset: 0,
      queryChanged: false,
      missingData
    };
  });
};
export class WorkflowAdapterConfig {
  constructor() {
    this.fetchedWorkflowsCache = new AdapterCache();
    this.performanceTrackingKey = 'WorkflowAdapterConfig';
    this.isAssetFamilySupported = assetFamily => {
      return hasPaginatedWorkflows() && DSAssetFamilies.WORKFLOW === assetFamily;
    };
  }
  getFetcher() {
    return http.post;
  }
  getCacheKeyForSearch(query, offset = 0) {
    return `workflow_search_${query || 'all'}_${offset}`;
  }
  fetchWorkflowsBySearch(httpClient, portalId, query, offset = 0, queryChanged = false, ids, getMissingFieldErrorMessage) {
    this.lastQuery = query;
    const requestBody = createWorkflowRequestBody(undefined, query, offset);
    const cacheKey = this.getCacheKeyForSearch(query, offset);
    const cachedResult = this.fetchedWorkflowsCache.get(cacheKey);
    // we want to refetch if the query has changed
    if (cachedResult && !queryChanged) {
      return cachedResult;
    }
    const searchPromise = httpClient(`crm-search/search?portalId=${portalId}`, {
      data: requestBody
    }).then(response => {
      const {
        referenceRecords
      } = processWorkflowResponse(response);
      const data = referenceRecords.reduce((acc, record) => {
        return acc.set(record.id, record);
      }, ImmutableMap());
      const missingData = getMissingFields(data, getMissingFieldErrorMessage, ids);
      return {
        data,
        hasMore: response.hasMore,
        offset: response.offset,
        queryChanged,
        missingData
      };
    });
    this.fetchedWorkflowsCache.set(cacheKey, searchPromise);
    return searchPromise;
  }
  fetchData(httpClient, _assetFamily, variables = {}) {
    const {
      offset = 0,
      query,
      ids,
      getMissingFieldErrorMessage
    } = variables;
    const portalId = PortalIdParser.get();
    if (portalId === undefined) {
      throw new Error('Portal ID is required');
    }
    const queryChanged = isQueryChanged(query, this.lastQuery);
    const actualOffset = queryChanged ? 0 : offset;

    // Handle fetching by specific IDs
    if (ids && ids.length > 0) {
      return fetchWorkflowsByIds(httpClient, portalId, ids, getMissingFieldErrorMessage);
    }

    // Handle search with query and pagination
    return this.fetchWorkflowsBySearch(httpClient, portalId, query, actualOffset, queryChanged, ids, getMissingFieldErrorMessage);
  }
  formatData({
    data: newData,
    hasMore: __hasMore,
    offset: __offset,
    queryChanged,
    missingData
  }, assetFamily, previousFormattedData) {
    const formattedNewData = toDSFieldRecord(newData, assetFamily);

    // Convert MissingField instances to DSFieldRecord format for merging
    const formattedMissingData = missingData.entrySeq().reduce((acc, [key, value]) => {
      return acc.set(key, value);
    }, ImmutableMap());
    const formattedNewDataWithMissingData = formattedNewData.merge(formattedMissingData);
    if (queryChanged) {
      return formattedNewDataWithMissingData;
    } else {
      return previousFormattedData.merge(formattedNewDataWithMissingData);
    }
  }
  clearCache() {
    this.lastQuery = undefined;
  }
  clearCacheAll() {
    this.clearCache();
  }
}
export default Adapter.makeFromConfig(WorkflowAdapterConfig);